import type { SearchResponse } from '@algolia/client-search';
import omitBy from 'lodash/omitBy';

import { AnalyticsMetadata } from '@/lib/personalization/dynamicYield';
import { ExperimentViewed, experimentViewed } from '@/rudder-typer';
import { gtag } from '@/utils/analytics';
import { identify } from '@/utils/analytics/rudderstack';

type ExperimentProvider = 'Builder.io' | 'Dynamic Yield' | 'Algolia';

export function sendExperimentViewedEvent(payload: ExperimentViewed, cms: ExperimentProvider) {
  const gtagPayload = {
    cms,
    content_id: payload.campaign_id,
    content_name: payload.campaign_name,
    experience_id: payload.experiment_id,
    experience_name: payload.experiment_name,
    test_variation_id: payload.variation_id,
    test_variation_name: payload.variation_name,
  };
  const filteredPayload = omitBy(gtagPayload, (value) => value === undefined);
  gtag('event', 'content_decision', filteredPayload);

  experimentViewed(payload);

  if (payload.experiment_name && payload.variation_name) {
    let key = payload.experiment_name;
    if (payload.campaign_name) key = `${payload.campaign_name} - ${key}`;

    identify(undefined, { [key]: payload.variation_name });
  }
}

export const trackedAlgoliaExperiments = new Set<number>();

/**
 * Tracks an Algolia A/B test in a way that is meaningful to humans.
 *
 * Algolia only sends IDs, even though the dashboard displays names. We don't have access to the
 * experiment name, so deriving one can only point to the ID that a user would find in the dashboard's
 * URL.
 *
 * However, we can derive the same variant name from the ID to match dashboard presentation. Algolia
 * documentation states that the variant ID is 1-indexed and that a value of 1 indicates the control
 * group. It is extremely unlikely that the experimental group will reach 26 variants, so this will
 * not account for anything beyond the basic alphabet capitalized.
 */
export function trackAlgoliaExperiment(response?: SearchResponse) {
  if (!response?.abTestID || !response?.abTestVariantID) return;

  if (trackedAlgoliaExperiments.has(response.abTestID) && !import.meta.env.SSR) return;
  trackedAlgoliaExperiments.add(response.abTestID);

  const variationLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const variationLetter = variationLetters[response.abTestVariantID - 1]; // -1 because it's 1-indexed
  const variationName = `Variant ${variationLetter}`;
  sendExperimentViewedEvent(
    {
      experiment_id: `${response.abTestID}`,
      experiment_name: `Algolia Search A/B Test: ${response.abTestID}`,
      variation_id: `${response.abTestVariantID}`,
      variation_name: variationName,
    },
    'Algolia',
  );
}

export const trackedDynamicYieldExperiments = new Set<number>();

export function trackDynamicYieldExperiment(analyticsMetadata?: AnalyticsMetadata) {
  if (!analyticsMetadata?.campaignId || !analyticsMetadata?.variationId) return;

  if (trackedDynamicYieldExperiments.has(analyticsMetadata.campaignId)) return;

  if (!import.meta.env.SSR) {
    trackedDynamicYieldExperiments.add(analyticsMetadata.campaignId);
  }

  sendExperimentViewedEvent(
    {
      campaign_id: `${analyticsMetadata.campaignId}`,
      campaign_name: analyticsMetadata.campaignName,
      experiment_id: `${analyticsMetadata.experienceId}`,
      experiment_name: analyticsMetadata.experienceName,
      variation_id: `${analyticsMetadata.variationId}`,
      variation_name: analyticsMetadata.variationName,
    },
    'Dynamic Yield',
  );
}
